import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2, CheckCircle2, Clock, Sparkles, LogOut, Trophy, Calendar,
  Send, X, ExternalLink, MessageSquare, Home, LayoutDashboard, Sparkle, UserCog, FolderOpen, Users, Menu
} from 'lucide-react';

const ROLE_INTRO = {
  social_media:       "Own Zazu's social voice today. Every clip you ship reaches real families.",
  sales:              "You're the tip of the sales spear today. Every conversation matters.",
  marketing:          "You're growing Zazu's organic funnel today. Ship one measurable thing.",
  content_curriculum: "You're building the lessons real kids will love today.",
  support:            "You're the friendly voice families hear when something breaks. Answer fast, answer kind.",
};

const InternDashboard = () => {
  const { api, user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [tasksDoc, setTasksDoc] = useState(null);
  const [hrStatus, setHrStatus] = useState({ completed: true, missing: [] });
  const [submitTask, setSubmitTask] = useState(null);
  const [submission, setSubmission] = useState('');
  const [saving, setSaving] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meRes, tasksRes, hrRes] = await Promise.all([
        api.get('/intern/me').catch(() => null),
        api.get('/intern/tasks/today').catch(() => null),
        api.get('/hr/me/profile').catch(() => null),
      ]);
      if (!meRes) {
        toast.error("You're not registered as an intern.");
        navigate('/dashboard');
        return;
      }
      setProfile(meRes.data);
      setTasksDoc(tasksRes?.data || null);
      if (hrRes?.data) {
        setHrStatus({
          completed: !!hrRes.data.completed,
          missing: hrRes.data.required_missing || [],
        });
      }
    } catch {
      toast.error('Failed to load intern dashboard');
    } finally {
      setLoading(false);
    }
  }, [api, navigate]);

  useEffect(() => { load(); }, [load]);

  const openSubmit = (task) => {
    setSubmitTask(task);
    setSubmission(task.submission || '');
  };

  const saveSubmission = async () => {
    if (!submitTask || !submission.trim()) return;
    setSaving(true);
    try {
      await api.post('/intern/tasks/submit', {
        task_id: submitTask.task_id,
        submission: submission.trim(),
      });
      toast.success('Task submitted!');
      setSubmitTask(null);
      setSubmission('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;
  }
  if (!profile) return null;

  const tasks = tasksDoc?.tasks || [];
  const done = tasks.filter((t) => t.status === 'done').length;
  const totalMinutes = tasks.reduce((s, t) => s + (t.est_minutes || 0), 0);
  const doneMinutes = tasks.filter((t) => t.status === 'done').reduce((s, t) => s + (t.est_minutes || 0), 0);
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;
  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-3 py-3 flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="font-semibold text-slate-800 flex items-center gap-2 text-sm sm:text-base truncate">
              <Sparkles className="w-4 h-4 text-emerald-500 flex-shrink-0" /> Intern Dashboard
            </h1>
            <p className="text-xs text-slate-500 truncate">{profile.role_label} · {profile.name}</p>
          </div>
          {/* Mobile hamburger — visible <sm only */}
          <div className="sm:hidden relative">
            <Button variant="ghost" size="sm" onClick={() => setMobileMenuOpen((o) => !o)} data-testid="intern-mobile-menu-btn" aria-label="Open menu" className="relative">
              <Menu className="w-5 h-5" />
              {!hrStatus.completed && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-rose-500 ring-2 ring-white" aria-hidden />
              )}
            </Button>
            {mobileMenuOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setMobileMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-xl shadow-xl z-40 py-1" data-testid="intern-mobile-menu">
                  {[
                    { label: 'Explore platform', icon: LayoutDashboard, onClick: () => { navigate('/intern/preview'); setMobileMenuOpen(false); }, testid: 'mobile-nav-platform' },
                    { label: 'Team', icon: Users, onClick: () => { navigate('/employee/team'); setMobileMenuOpen(false); }, testid: 'mobile-nav-team' },
                    { label: 'Resources', icon: FolderOpen, onClick: () => { navigate('/employee/resources'); setMobileMenuOpen(false); }, testid: 'mobile-nav-resources' },
                    { label: hrStatus.completed ? 'HR profile' : 'HR profile — incomplete', icon: UserCog, onClick: () => { navigate('/employee/hr-profile'); setMobileMenuOpen(false); }, testid: 'mobile-nav-hr', badge: !hrStatus.completed },
                    { label: 'Team chat', icon: MessageSquare, onClick: () => { navigate('/employee/chat'); setMobileMenuOpen(false); }, testid: 'mobile-nav-chat' },
                    { label: 'Log out', icon: LogOut, onClick: () => { logout(); }, testid: 'mobile-nav-logout', danger: true },
                  ].map((it, i) => {
                    const Icon = it.icon;
                    return (
                      <button
                        key={i}
                        onClick={it.onClick}
                        className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 ${it.danger ? 'text-rose-600' : 'text-slate-700'}`}
                        data-testid={it.testid}
                      >
                        <Icon className="w-4 h-4" /> {it.label}
                        {it.badge && <span className="ml-auto w-2 h-2 rounded-full bg-rose-500" />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          {/* Desktop buttons — hidden on mobile */}
          <div className="hidden sm:flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => navigate('/intern/preview')} data-testid="intern-open-platform">
              <LayoutDashboard className="w-4 h-4 mr-1" /> Explore
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/employee/team')} data-testid="intern-open-team">
              <Users className="w-4 h-4 mr-1" /> Team
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/employee/resources')} data-testid="intern-open-resources">
              <FolderOpen className="w-4 h-4 mr-1" /> Resources
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/employee/hr-profile')}
              data-testid="intern-open-hr-profile"
              className="relative"
              title={hrStatus.completed ? 'HR profile' : 'HR profile — incomplete'}
            >
              <UserCog className="w-4 h-4 mr-1" /> HR profile
              {!hrStatus.completed && (
                <span
                  className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5"
                  data-testid="hr-incomplete-dot"
                  aria-label="HR profile incomplete"
                >
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500 ring-2 ring-white" />
                </span>
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/employee/chat')} data-testid="intern-open-chat">
              <MessageSquare className="w-4 h-4 mr-1" /> Team chat
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="intern-logout">
              <LogOut className="w-4 h-4 mr-1" /> Log out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 md:p-6 space-y-6">
        {/* Hero */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 via-white to-teal-50">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <Badge className="bg-emerald-100 text-emerald-700 mb-2"><Calendar className="w-3 h-3 mr-1" /> {today}</Badge>
                <h2 className="font-fredoka text-3xl font-bold text-slate-900">Welcome back, {profile.name?.split(' ')[0] || 'friend'}!</h2>
                <p className="text-slate-600 mt-1">{ROLE_INTRO[profile.role_key] || "Let&apos;s ship great work today."}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <p className="text-xs text-slate-500">Today&apos;s progress</p>
                <p className="text-2xl font-bold text-emerald-600">{done} / {tasks.length}</p>
                <p className="text-xs text-slate-500">{doneMinutes} / {totalMinutes} min</p>
              </div>
            </div>
            <Progress value={pct} className="h-2 mt-4" />
          </CardContent>
        </Card>

        {/* Permission list */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-500 uppercase tracking-wide">Your access</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {(profile.permissions || []).map((p) => (
                <Badge key={p} variant="outline" className="font-mono text-xs">{p}</Badge>
              ))}
              {(!profile.permissions || profile.permissions.length === 0) && (
                <p className="text-sm text-slate-400 italic">No permissions assigned yet.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tasks */}
        <div>
          <h3 className="font-fredoka text-xl font-bold text-slate-800 mb-3 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" /> Today&apos;s Tasks
          </h3>
          <div className="space-y-3">
            {tasks.length === 0 && (
              <Card><CardContent className="p-8 text-center text-slate-500">No tasks today. Enjoy the break!</CardContent></Card>
            )}
            {tasks.map((t, i) => {
              const done = t.status === 'done';
              return (
                <Card
                  key={t.task_id}
                  className={`border-l-4 ${done ? 'border-emerald-500 bg-emerald-50/40' : 'border-amber-400 bg-white'}`}
                  data-testid={`task-card-${t.key}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${done ? 'bg-emerald-500 text-white' : 'bg-amber-100 text-amber-600'}`}>
                        {done ? <CheckCircle2 className="w-5 h-5" /> : <span className="font-bold">{i + 1}</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h4 className={`font-semibold text-slate-800 ${done ? 'line-through decoration-emerald-500 opacity-60' : ''}`}>
                              {t.title}
                            </h4>
                            {t.origin && t.origin !== 'template' && (
                              <Badge className="mt-1 bg-purple-100 text-purple-700 text-[10px]" data-testid={`task-origin-${t.task_id}`}>
                                <Sparkle className="w-3 h-3 mr-1" /> auto-generated · {t.origin.replace('_', ' ')}
                              </Badge>
                            )}
                          </div>
                          <Badge variant="outline" className="text-xs flex-shrink-0"><Clock className="w-3 h-3 mr-1" />{t.est_minutes} min</Badge>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">{t.detail}</p>
                        {done && t.submission && (
                          <div className="mt-3 p-3 rounded-lg bg-white border border-emerald-200 text-sm text-slate-700">
                            <p className="text-xs font-semibold text-emerald-700 mb-1">Your submission</p>
                            <p className="whitespace-pre-wrap">{t.submission}</p>
                          </div>
                        )}
                        <div className="mt-3 flex gap-2 flex-wrap">
                          {!done && (
                            <Button size="sm" onClick={() => openSubmit(t)} className="bg-emerald-500 hover:bg-emerald-600" data-testid={`submit-task-${t.key}`}>
                              <Send className="w-3 h-3 mr-1" /> Mark done
                            </Button>
                          )}
                          {done && (
                            <Button size="sm" variant="outline" onClick={() => openSubmit(t)}>
                              Edit submission
                            </Button>
                          )}
                          {t.link ? (
                            /^https?:\/\//.test(t.link) ? (
                              <Button size="sm" variant="outline" asChild data-testid={`goto-task-${t.task_id}`}>
                                <a href={t.link} target="_blank" rel="noreferrer">
                                  <ExternalLink className="w-3 h-3 mr-1" /> {t.link_label || 'Open'}
                                </a>
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  // First-day explore task: mark done automatically when the intern starts the tour.
                                  if (t.key === 'explore_platform_first_day' && !done) {
                                    try {
                                      await api.post('/intern/tasks/submit', { task_id: t.task_id, submission: 'Started platform tour' });
                                    } catch (e) { /* non-blocking */ }
                                  }
                                  navigate(t.link);
                                }}
                                data-testid={`goto-task-${t.task_id}`}
                              >
                                <Home className="w-3 h-3 mr-1" /> {t.link_label || 'Go to location'}
                              </Button>
                            )
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </main>

      {/* Submit dialog */}
      <Dialog open={!!submitTask} onOpenChange={(o) => !o && setSubmitTask(null)}>
        <DialogContent className="max-w-lg" data-testid="submit-task-dialog">
          {submitTask && (
            <>
              <DialogHeader>
                <DialogTitle>{submitTask.title}</DialogTitle>
                <DialogDescription>{submitTask.detail}</DialogDescription>
              </DialogHeader>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase">Your submission</label>
                <Textarea
                  value={submission}
                  onChange={(e) => setSubmission(e.target.value)}
                  rows={6}
                  placeholder="Paste your link, notes, or a short description of what you did."
                  data-testid="submission-input"
                />
                <p className="text-xs text-slate-400 mt-1">Include Canva links, doc URLs, or notes so your mentor can review.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSubmitTask(null)}><X className="w-4 h-4 mr-1" /> Cancel</Button>
                <Button onClick={saveSubmission} disabled={saving || !submission.trim()} className="bg-emerald-500 hover:bg-emerald-600" data-testid="submission-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                  Submit
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InternDashboard;
